import { supabase } from '@/shared/lib/supabase';
import { AppError, ValidationError } from '@/shared/lib/errors';
import { logger } from '@/shared/lib/logger';
import { normalizeEmail } from '@shared/email';
import {
  signInWithEmailSchema,
  signUpWithEmailSchema,
  type SignInWithEmail,
  type SignUpWithEmail,
} from '@shared/schemas/participants';

/**
 * Devuelve la URL de redirect para OAuth flows.
 * IMPORTANTE: usa import.meta.env.BASE_URL para respetar despliegues en /beta/.
 * (Lección aprendida de ManuJob).
 */
export function getAuthRedirectUri(): string {
  const base = import.meta.env.BASE_URL;
  return new URL(`${base}auth/callback`, window.location.origin).toString();
}

// ==================== Sign in ====================

export async function signInWithEmail(input: SignInWithEmail): Promise<void> {
  const parsed = signInWithEmailSchema.safeParse({
    email: normalizeEmail(input.email),
    password: input.password,
  });
  if (!parsed.success) {
    throw new ValidationError('Datos de inicio de sesión inválidos', {
      issues: parsed.error.flatten().fieldErrors,
    });
  }

  const { error } = await supabase.auth.signInWithPassword(parsed.data);
  if (error) {
    logger.warn('signInWithEmail failed', { code: error.code, message: error.message });
    throw new ValidationError('Email o contraseña incorrectos');
  }
}

/**
 * Actualiza la contraseña del usuario autenticado (llamado desde
 * ResetPasswordPage después de que el usuario haya aterrizado desde el email
 * de recuperación con la sesión temporal que Supabase le establece).
 */
export async function updatePassword(newPassword: string): Promise<void> {
  if (!newPassword || newPassword.length < 8) {
    throw new ValidationError('La contraseña debe tener al menos 8 caracteres');
  }
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    logger.warn('updatePassword failed', { code: error.code, message: error.message });
    throw new ValidationError('No se pudo actualizar la contraseña');
  }
}

export async function signInWithGoogle(): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getAuthRedirectUri(),
      queryParams: { access_type: 'offline', prompt: 'consent' },
    },
  });
  if (error) {
    logger.error('signInWithGoogle failed', { message: error.message });
    throw new ValidationError('No se pudo iniciar sesión con Google');
  }
}

export async function signInWithMicrosoft(): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: { redirectTo: getAuthRedirectUri(), scopes: 'email profile openid' },
  });
  if (error) {
    logger.error('signInWithMicrosoft failed', { message: error.message });
    throw new ValidationError('No se pudo iniciar sesión con Microsoft');
  }
}

// ==================== Sign up ====================

export async function signUpWithEmail(input: SignUpWithEmail): Promise<void> {
  const parsed = signUpWithEmailSchema.safeParse({
    ...input,
    email: normalizeEmail(input.email),
  });
  if (!parsed.success) {
    throw new ValidationError('Datos de registro inválidos', {
      issues: parsed.error.flatten().fieldErrors,
    });
  }

  const { error } = await supabase.auth.signUp({
    email: parsed.data.email,
    password: parsed.data.password,
    options: {
      data: { display_name: parsed.data.display_name },
      emailRedirectTo: getAuthRedirectUri(),
    },
  });
  if (error) {
    logger.warn('signUpWithEmail failed', { code: error.code, message: error.message });
    // Errores conocidos de Supabase
    if (error.message.toLowerCase().includes('already registered')) {
      throw new ValidationError('Ese email ya está registrado');
    }
    throw new ValidationError('No se pudo crear la cuenta');
  }
}

// ==================== Consentimiento RGPD ====================

/**
 * Registra el consentimiento del usuario en sesión.
 *
 * **Lanza si el servidor rechaza**, y ése es todo el motivo de que exista en
 * vez de llamar a `supabase.rpc` desde el componente. Hasta el 2026-08-28 la
 * función de la base de datos fallaba en *todas* las llamadas —metía un
 * `timestamptz` en una variable `uuid`— y nadie se enteró en cinco meses
 * porque los dos sitios que la llamaban lo hacían con `void` o dentro de un
 * `.then`, y `supabase-js` **no rechaza**: resuelve con `{ data, error }`. Un
 * error así no se ve: la pantalla se queda igual que si no hubieras pulsado.
 *
 * Convertido en `throw`, la llamada entra por el `MutationCache.onError` de
 * `providers.tsx`, que ya registra en `client_errors` — o sea que el siguiente
 * fallo de esta RPC llega por correo en minutos en vez de descubrirse contando
 * filas. Cero mecanismos nuevos: es el que ya había.
 */
export async function recordGdprConsent(): Promise<void> {
  const { error } = await supabase.rpc('record_gdpr_consent');
  if (error) {
    logger.error('recordGdprConsent failed', { code: error.code, message: error.message });
    // `AppError` y no `new Error`: es la jerarquía tipada de
    // `docs/conventions.md` §Errores, y en este fichero la usan los otros nueve
    // lanzamientos. No es `ValidationError` porque no lo es: aquí el servidor
    // rechaza, no el usuario se equivoca.
    //
    // Y el `code` es **el SQLSTATE**, no un código inventado del dominio: es lo
    // que lee `isRlsDeniedError` para decidir si el manejador global de
    // mutaciones enseña un toast de permisos o lo registra en `client_errors`.
    // Poner ahí otra cosa deja ese campo ocupado por algo que lo parece, y
    // esconde el de verdad en `context`, donde no mira nadie.
    throw new AppError(error.code, error.message, 500, { rpc: 'record_gdpr_consent' });
  }
}

// ==================== Sign out ====================

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) {
    logger.warn('signOut failed', { message: error.message });
  }
}

// ==================== Password reset ====================

export async function requestPasswordReset(email: string): Promise<void> {
  const normalized = normalizeEmail(email);
  const base = import.meta.env.BASE_URL;
  const redirectTo = new URL(`${base}auth/reset-password`, window.location.origin).toString();
  const { error } = await supabase.auth.resetPasswordForEmail(normalized, {
    redirectTo,
  });
  if (error) {
    logger.warn('requestPasswordReset failed', { message: error.message });
    // No revelamos si el email existe o no (política de seguridad)
  }
}
